import { Box, Grid, InputAdornment } from '@mui/material';
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { routeName } from 'routes/routes-name';

import _ from 'lodash';
import moment from 'moment';

/** TRANSLATION*/
import { useTranslation } from 'react-i18next';

/**API */
import ClinicApi from 'api/master/clinic.api';
import ReportApi, { FindAllReportInterface } from 'api/report.api';
import { getBranch } from 'utils/app.utils';

/**COMPONENT */
import HeaderCard from 'component/CardCustom/HeaderCard';
import { useDownloadExcel } from 'component/Excel/hooks/useExcel';
import Loading from 'component/Loading';

/**STYLE */
import TextInput from 'new-components/inputs/text-input';
/** CONSTANTS */
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DropdownButton from 'new-components/buttons/dropdown-button';
import MainButton from 'new-components/buttons/main-button';
import SelectInput from 'new-components/inputs/select-input';
import SelectCheckBoxInput from 'new-components/inputs/select_checkbox_input';
import { UseSnCode } from 'utils/useSnCode';
import PatientDateOfBirthList from './PatientDateOfBirthList';
import ExportToExcel from './exportToExcel';



type Doctors = {
    userId: number
    firstname: string
    lastname: string
    fullname: string
}

export interface DentistInterface {
    dentistId: number
    dentistFullname: string
}

export default function PatientDateOfBirth() {



    const { t } = useTranslation();

    const history = useHistory();
    const componentRef = useRef<HTMLDivElement>(null);
    const momentNow = moment().format('YYYY-MM-DD');

    const [branchId, setBranchId] = useState<any>(getBranch());
    const [branches, setBranches] = useState<any[]>([]);
    const [rowCount, setRowCount] = useState<number>(0);
    const [sortBy, setSortBy] = useState<string>('createAt');
    const [sortType, setSortType] = useState<string>('ASC');

    const [search, setSearch] = useState<string>('');
    const [patientDateOfBirthData, setPatientDateOfBirthData] = useState<any>({ rows: [] });
    const [patientDateOfBirthDataExport, setPatientDateOfBirthDataExport] = useState<any>({ rows: [] });
    const [dateRange, setLabDateRange] = useState<string>(momentNow);
    const [dataPage, setDataPage] = useState<number>(1);
    const [dataPageLimit, setDataPageLimit] = useState<number>(10);
    const [dataSecondPage, setDataSecondPage] = useState<number>(1);

    const [loading, setLoading] = useState<boolean>(true);
    const [fetch, setFetch] = useState<boolean>(false);

    const [selectedMonths, setSelectedMonths] = useState<(number | string)[]>(Array.from({ length: 12 }, (_, i) => i + 1));
    const [snType, setSnType] = useState(false);

    const handleSnCode = useCallback(() => {
        const fetchData = async () => {
            const sn = await UseSnCode();
            setSnType(sn);
        };

        fetchData();

    }, [snType]);

    useEffect(() => {
        handleSnCode()
    }, [])


    const verifyPermission = async () => {
        const condition: FindAllReportInterface = {
            page: 1, pageLimit: 200
        }
        ReportApi.reportList(condition)
            .then(({ data }) => {
                const check = _.find(data, (val: any) => String(val.reportKey) === 'PATIENT_DATE_OF_BIRTH') ? true : false
                if (!check) {
                    history.push(routeName.report)
                    return false
                }
            })
        ClinicApi.findAllBranches()
            .then(({ data }) => setBranches(data))
            .catch((e) => { return })

        return true
    }

    useEffect(() => {
        verifyPermission()
    }, [])

    const handleMonthChange = (event: ChangeEvent<{ value: unknown }>) => {
        const selectedValues = event.target.value as (number | string)[];

        if (selectedValues.includes("ALL")) {
            if (selectedMonths.length === 12) { // ถ้าเลือกทั้งหมดอยู่แล้ว
                setSelectedMonths([]); // ยกเลิกการเลือกทั้งหมด
            } else {
                setSelectedMonths(Array.from({ length: 12 }, (_, i) => i + 1)); // เลือกทั้งหมด
            }
        } else {
            setSelectedMonths(selectedValues);
        }
    };


    const fetchPatientDateOfBirth = useCallback(async () => {
        await verifyPermission()
        let condition: FindAllReportInterface = {
            page: dataPage, pageLimit: dataPageLimit, search: search,
            date: String(selectedMonths)
        }
        if (branchId) condition = { ...condition, branchId: branchId }
        if (sortBy) condition = { ...condition, sortBy: sortBy }
        if (sortType) condition = { ...condition, sortType: sortType }
        ReportApi.reportPatientDateOfBirth(condition)
            .then(({ headers, data }) => {
                setPatientDateOfBirthData(data)
                setRowCount(headers['x-total'])
            }).catch((e) => setLoading(false))
            .finally(() => setLoading(false))

        setFetch(false)
    }, [dataPage, dataSecondPage, dataPageLimit, fetch])

    const fetchPatientDateOfBirthExport = useCallback(async () => {
        await verifyPermission()
        let condition: FindAllReportInterface = {
            page: dataPage, pageLimit: 1000, search: search, // จำกัดจำนวนเพื่อป้องกัน memory issue
            date: String(selectedMonths), export: 1
        }
        if (branchId) condition = { ...condition, branchId: branchId }
        if (sortBy) condition = { ...condition, sortBy: sortBy }
        if (sortType) condition = { ...condition, sortType: sortType }
        ReportApi.reportPatientDateOfBirth(condition)
            .then(({ headers, data }) => {
                setPatientDateOfBirthDataExport(data)
                setRowCount(headers['x-total'])
            }).catch((e) => setLoading(false))
            .finally(() => setLoading(false))

        setFetch(false)
    }, [dataPage, dataSecondPage, dataPageLimit, fetch])


    const { onDownload } = useDownloadExcel({
        tableExportRef: componentRef.current,
        filename: `${t(`REPORT.REPORT_NAME.PATIENT_DATE_OF_BIRTH`)}`,
        sheetName: `${t(`REPORT.REPORT_NAME.PATIENT_DATE_OF_BIRTH`)}`
    })

    const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
        setSortType(sortTypeVal)
        setSortBy(sortByVal)
    }

    const handleChangeRowsPerPage = (limit: number) => setDataPageLimit(limit)
    const handleChangePage = (val: number) => setDataPage(val)

    useEffect(() => {
        fetchPatientDateOfBirthExport()
        fetchPatientDateOfBirth()
    }, [fetchPatientDateOfBirth, fetchPatientDateOfBirthExport])

    // โหลดข้อมูล export เมื่อมีการเปลี่ยนแปลง filter
    useEffect(() => {
        if (fetch) {
            fetchPatientDateOfBirthExport()
        }
    }, [fetch, fetchPatientDateOfBirthExport])

    const exportExcel = useCallback(async () => {
        // ตรวจสอบว่าข้อมูลถูกโหลดแล้วหรือไม่
        if (!patientDateOfBirthDataExport?.rows || patientDateOfBirthDataExport.rows.length === 0) {
            console.warn('ไม่มีข้อมูลสำหรับการส่งออก');
            alert('ไม่มีข้อมูลสำหรับการส่งออก กรุณากดปุ่มค้นหาก่อน');
            return;
        }
        const filter = [
            `${t(`REPORT.REPORT_NAME.PATIENT_DATE_OF_BIRTH`)}`,
            branchId === 0 ? ` ${t('REPORT.REPORT_GROUP.ALL')}` : `${_.get(_.find(branches, { branchId: branchId }), 'branchName', 'branchNameEn')}`,
            ((selected) => {
                if (Array.isArray(selected)) {
                    if (selected.length === 0) return `${t('REPORT.MONTH.NULL')}`;
                    if (selected.length === 12 || selected.includes('ALL')) return `${t('REPORT.REPORT_GROUP.SELECT_ALL')}`;
                    return ` ${selected.map(monthNum => t(`REPORT.MONTH.${monthNum}`)).join(', ')}`;
                }
                return '';
            })(selectedMonths)
        ];
        try {
            await ExportToExcel({
                data: patientDateOfBirthDataExport.rows,
                filter,
                snType,
                t
            });
        } catch (error) {
            console.error('เกิดข้อผิดพลาดในการส่งออกไฟล์ Excel:', error);
            alert('เกิดข้อผิดพลาดในการส่งออกไฟล์ Excel: ' + (error as Error).message);
        }
    }, [patientDateOfBirthDataExport, branchId, branches, selectedMonths, snType, t]);

    return (
        <Box>
            <HeaderCard text={t(`REPORT.REPORT_NAME.PATIENT_DATE_OF_BIRTH`)} />
            <Box px={3} pt={2}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={6} xl={3}>
                        <TextInput
                            label={t('REPORT.REPORT_NAME.SEARCH_PATIENT_DATE_OF_BIRTH')}
                            value={search}
                            onChange={(event) => {
                                setSearch(event.target.value)
                            }}
                            fullWidth
                            inputProps={{
                                endAdornment: (
                                    <InputAdornment position="end" >
                                        {!search ? <FontAwesomeIcon icon={faSearch} style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 15 }} /> :
                                            <FontAwesomeIcon
                                                icon={faTimes}
                                                onClick={() => {
                                                    setSearch('');
                                                }}
                                                style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 17, cursor: 'pointer' }} />}
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6} xl={3}>
                        <SelectInput
                            label={t('REPORT.FILTER.BRANCH')}
                            value={branchId}
                            onChange={e => setBranchId(Number(e.target.value))}
                            options={[
                                ...branches.map(b => ({ value: b.branchId, name: b.branchName || b.branchNameEn }))
                            ]}
                            width="100%"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6} xl={3}>
                        <SelectCheckBoxInput
                            options={Array.from({ length: 12 }, (_, i) => ({
                                id: i + 1,
                                name: t(`REPORT.MONTH.${i + 1}`),
                            }))}
                            selected={selectedMonths}
                            onChange={(values) => setSelectedMonths(values as number[])}
                            fullWidth
                            label={t('REPORT.MONTH.DATE_OF_BIRTH')}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6} xl={3}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                            <MainButton
                                title={t('INPUT.SEARCH')}
                                onClick={() => {
                                    setFetch(true)
                                    setDataPage(1)
                                    setLoading(true)
                                    // เรียกใช้ fetchPatientDateOfBirthExport เพื่อโหลดข้อมูลสำหรับ export
                                    setTimeout(() => {
                                        fetchPatientDateOfBirthExport()
                                    }, 100)
                                }}
                                type={'primary'}
                                variant={'contained'}
                                style={{ width: '120px' }}
                                disabled={selectedMonths.length === 0}
                            />
                            <DropdownButton title='พิมพ์เอกสาร' onChange={(e) => {
                                if (e === 'Excel') {
                                    exportExcel()
                                } else if (e === 'PDF') {
                                    const style = document.createElement("style");
                                    style.innerHTML = `
                      @media print {
                        @page { size: landscape }
                      }
                    `;
                                    document.head.appendChild(style);
                                    window.print();
                                    document.head.removeChild(style);

                                }
                            }} options={[{ value: 'Excel', name: ' Excel' }, { value: 'PDF', name: ' PDF' }]} />
                        </Box>
                    </Grid>
                </Grid>
                <Box marginTop="16px">
                    <PatientDateOfBirthList
                        page={dataPage}
                        pageLimit={dataPageLimit}
                        rowCount={rowCount}
                        sortBy={sortBy}
                        sortType={sortType}
                        data={patientDateOfBirthData}
                        dataExport={patientDateOfBirthDataExport}
                        filter={[
                            `${t(`REPORT.REPORT_NAME.PATIENT_DATE_OF_BIRTH`)}`,
                            branchId === 0 ? ` ${t('REPORT.REPORT_GROUP.ALL')}` : `${_.get(_.find(branches, { branchId: branchId }), 'branchName', 'branchNameEn')}`,
                            ((selected) => {
                                if (Array.isArray(selected)) {
                                    if (selected.length === 0) return `${t('REPORT.MONTH.NULL')}`;
                                    if (selected.length === 12 || selected.includes('ALL')) return `${t('REPORT.REPORT_GROUP.SELECT_ALL')}`;
                                    return ` ${selected.map(monthNum => t(`REPORT.MONTH.${monthNum}`)).join(', ')}`;
                                }
                                return '';
                            })(selectedMonths)
                        ]}

                        onRequestSort={onRequestSort}
                        handleChangeRowsPerPage={handleChangeRowsPerPage}
                        handleChangePage={handleChangePage}
                        componentRef={componentRef}
                    />
                </Box>
            </Box>
            <Loading show={loading} type="fullLoading" />
        </Box >
    )
}